import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {interval, ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {RegistrationIdImport} from '../../../../shared/interface/registration-id-import';
import {CommissionImport} from '../../../../shared/interface/commission-import';
import {CommissionImportService} from '../../../../shared/services/commission-import.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {CommissionImportRowService} from '../../../../shared/services/commission-import-row.service';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {FormHelperService} from "../../../../shared/services/form-helper.service";
import moment from "moment";
import {ProgressBarMode} from "@angular/material/progress-bar";
import {ThemePalette} from "@angular/material/core";
import {COMMISSION_IMPORT_ROW_STATUS_ASSIGNED, IMPORT_STATUS_IN_PROGRESS} from "../../../../shared/services/dict.service";

@Component({
  selector: 'app-all-imports-list',
  templateUrl: './all-imports-list.component.html',
  styleUrls: ['./all-imports-list.component.scss']
})
export class AllImportsListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  imports: CommissionImport[] = [];
  displayedColumns: string[] = [
    'billingMonth', 'name', 'company', 'insuranceCompany', 'importer', 'createdAt', 'createdBy', 'sum', 'totalRows', 'status', 'actions'
  ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  formErrors = {};
  color: ThemePalette = 'accent';
  mode: ProgressBarMode = 'indeterminate';
  control: FormControl;
  progressBarDelete: string;
  readonly IMPORT_STATUS_IN_PROGRESS = IMPORT_STATUS_IN_PROGRESS;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionImportService: CommissionImportService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private commissionImportRowService: CommissionImportRowService,
    private formHelper: FormHelperService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionImportService.importListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionImport>) => {
          this.imports = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();

    // const date = moment();
    // date.startOf('month');
    // date.subtract(1, 'month');
    //
    // this.initialFilters.billingMonth = date;
    // this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
    //   billingMonth: this.initialFilters.billingMonth,
    //   ...this.initialFilters,
    // })]);

  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$name: 'string',
      company: 'int',
      in$insuranceCompany: 'int[]',
      gte$createdAt: 'string',
      lte$createdAt: 'string',
      createdBy: 'string',
      status: 'int',
      in$importer: 'int[]',
      billingMonth: 'date',
      importType: 'string[]',
    }, filters);
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0) {
          this.loadListData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.commissionImportService.loadImportList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.commissionImportService.downloadImportsReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadErrorsReport(commissionImport: CommissionImport): void {
    this.commissionImportService.downloadErrorsReport(commissionImport.uuid)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  onImgError($event: ErrorEvent): void {
    // @ts-ignore
    $event.target.src = '/assets/avatar.png';
  }

  downloadImportFile(registrationIdImport: RegistrationIdImport): void {
    this.commissionImportService.downloadImportFile(registrationIdImport.uuid);
  }

  deleteImport(commissionImport: CommissionImport): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć import?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.progressBarDelete = commissionImport.uuid;
        this.commissionImportService.deleteImport(commissionImport.uuid)
          .subscribe((response: Uuid) => {
            const dialogRefv1 = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRefv1.afterClosed().subscribe();
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 4000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
        // this.commissionImportService.deleteImport(commissionImport.uuid)
        //   .subscribe(() => {
        //     this.snack.open('Pomyślnie usunięto import', '', {duration: 2000});
        //     this.loadListData();
        //   }, (error: HttpErrorResponse) => {
        //     this.snack.open('Podczas usuwania pliku wystąpił błąd', error.status.toString(), {
        //       duration: 2000,
        //     });
        //
        //     if (error.status === 400) {
        //       this.snack.open(error.error.error.text, null, {duration: 4000});
        //     }
        //   });
      }
    });
  }

  downloadReportFile(commissionImport: CommissionImport): void {
    this.commissionImportRowService.downloadReport({import: commissionImport.uuid})
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  restartImport(commissionImport: CommissionImport): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz ponownie zaimportować plik?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commissionImportService.restartImport(commissionImport.uuid)
          .subscribe({
            next: () => {
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  trackTask(index: number, commissionImport: CommissionImport): string {
    return commissionImport.uuid;
  }

}
